import { useContext } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TriangleAlert } from 'lucide-react';

import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { DuplicateMetadata } from 'lib/api/validateAppt';
import { Maybe, Undef } from 'types/UtilityTypes';
import { parseToUtcWallClockDate } from 'utils/parseToUtcWallClockDate';

dayjs.extend(utc);

type YardViewAppointmentBannerProps = {
  variant: 'success' | 'warning';
  confirmationNumber?: Undef<string>;
  appointmentUrl?: Undef<string>;
  duplicateMetadata?: Undef<DuplicateMetadata>;
  tmsUpdateSucceeded?: boolean;
  tmsUpdatePending?: boolean;
};

function formatDuplicateAppointmentTime(
  appointmentTime: string
): Maybe<string> {
  // YardView duplicate times are warehouse-local wall clock (including Z/offset).
  // parseToUtcWallClockDate keeps the literal digits and ignores the zone suffix.
  const wallClockDate = parseToUtcWallClockDate(appointmentTime);
  if (!wallClockDate) {
    return null;
  }

  return dayjs.utc(wallClockDate).format('MMM D, YYYY, h:mm A');
}

function getDuplicateDetailsCopy(duplicateMetadata: DuplicateMetadata): string {
  const confirmation = `Confirmation #${duplicateMetadata.confirmationNo}`;
  if (!duplicateMetadata.appointmentTime) {
    return confirmation;
  }

  const formattedAppointmentTime = formatDuplicateAppointmentTime(
    duplicateMetadata.appointmentTime
  );
  if (!formattedAppointmentTime) {
    return confirmation;
  }

  return `${formattedAppointmentTime} · ${confirmation}`;
}

function getTmsUpdateCopy(
  tmsUpdatePending: boolean,
  tmsUpdateSucceeded: boolean
): string {
  if (tmsUpdatePending) {
    return 'Your TMS update will run in the background.';
  }
  if (tmsUpdateSucceeded) {
    return 'Your TMS was updated with the appointment details';
  }
  return 'Make sure to update your TMS with the scheduled appointment.';
}

export function YardViewAppointmentBanner({
  variant,
  confirmationNumber,
  appointmentUrl,
  duplicateMetadata,
  tmsUpdateSucceeded = false,
  tmsUpdatePending = false,
}: YardViewAppointmentBannerProps) {
  const {
    currentState: { openExternalUrl },
  } = useContext(SidebarStateContext);

  if (variant === 'success') {
    return (
      <Flex
        direction='col'
        gap='xs'
        className='my-3 rounded bg-success-200 px-4 py-3 text-neutral-700'
      >
        <Typography variant='body-sm'>Appointment confirmed 🎉</Typography>
        <Typography variant='body-sm' weight='semibold'>
          YardView Confirmation #: {confirmationNumber}
        </Typography>
        <Typography variant='body-sm'>
          {getTmsUpdateCopy(tmsUpdatePending, tmsUpdateSucceeded)}
        </Typography>
        {appointmentUrl && (
          <Button
            type='button'
            variant='unstyled'
            size='auto'
            buttonNamePosthog={null}
            className='w-fit text-left text-accent underline'
            onClick={() => openExternalUrl(appointmentUrl)}
          >
            Visit the appointment page
          </Button>
        )}
      </Flex>
    );
  }

  if (!duplicateMetadata) {
    return null;
  }

  return (
    <div className='mt-2 rounded-[4px] border border-warning-300 bg-warning-100 px-2.5 py-1.5 shadow-sm'>
      <Flex direction='col' gap='none'>
        <Flex direction='row' gap='xs' align='center'>
          <TriangleAlert
            className='h-3.5 w-3.5 shrink-0 text-warning-600'
            strokeWidth={2.5}
          />
          <Typography variant='body-xs' weight='semibold'>
            This Pro # already has a YardView appointment
          </Typography>
        </Flex>

        <Flex direction='col' gap='none' className='ml-0.5'>
          <Typography
            variant='body-xs'
            weight='medium'
            className='text-neutral-600'
          >
            {getDuplicateDetailsCopy(duplicateMetadata)}
          </Typography>
          <Typography variant='body-xs' className='text-neutral-600'>
            Already scheduled, so it can't be booked again here.
          </Typography>
          <Button
            type='button'
            variant='unstyled'
            size='auto'
            buttonNamePosthog={null}
            className='mt-0.5 w-fit text-left text-xs text-accent underline'
            onClick={() => openExternalUrl(duplicateMetadata.appointmentUrl)}
          >
            Reschedule in YardView
          </Button>
        </Flex>
      </Flex>
    </div>
  );
}
